'use client';

import { cn, convertPersianToEnglish, formatPrice } from '@insta-club/utilities';
import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef } from 'react';
import { useFormContext } from 'react-hook-form';

type InputType = 'text' | 'number' | 'persian' | 'english' | 'price';

const containerVariants = cva(
    'w-full relative rounded-lg border px-3 focus-within:border-primary-500 bg-transparent text-sm transition-all duration-200',
    {
        variants: {
            variant: {
                default: 'border-gray-300 focus-within:border-primary-500',
                error: 'border-red-500 focus-within:border-red-500'
            },
            size: {
                default: 'h-14',
                sm: 'h-12',
                lg: 'h-16'
            }
        },
        defaultVariants: {
            variant: 'default',
            size: 'default'
        }
    }
);

const floatingLabelVariants = cva(
    'absolute right-3 text-gray-500 transition-all duration-200 pointer-events-none bg-white px-1',
    {
        variants: {
            variant: {
                default: 'peer-focus:text-primary-500',
                error: 'text-red-500 peer-focus:text-red-500'
            },
            size: {
                default:
                    'top-4 peer-focus:-top-2 peer-focus:text-xs peer-[:not(:placeholder-shown)]:-top-2 peer-[:not(:placeholder-shown)]:text-xs',
                sm: 'top-3 peer-focus:-top-2 peer-focus:text-xs peer-[:not(:placeholder-shown)]:-top-2 peer-[:not(:placeholder-shown)]:text-xs',
                lg: 'top-5 peer-focus:-top-2 peer-focus:text-xs peer-[:not(:placeholder-shown)]:-top-2 peer-[:not(:placeholder-shown)]:text-xs'
            }
        },
        defaultVariants: {
            variant: 'default',
            size: 'default'
        }
    }
);

export interface FloatingInputProps
    extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'>,
        VariantProps<typeof containerVariants> {
    label?: string;
    containerClassName?: string;
    inputType?: InputType;
    name: string;
    startAdornment?: React.ReactElement;
    endAdornment?: React.ReactElement;
    fullWidth?: boolean;
}

const validateInput = (value: string, inputType: InputType): string => {
    const withEnglishNumbers = convertPersianToEnglish(value);

    switch (inputType) {
        case 'number': {
            return withEnglishNumbers.replace(/[^\d]/g, '');
        }
        case 'price': {
            return formatPrice(withEnglishNumbers);
        }
        case 'persian':
            return withEnglishNumbers.replace(/[^\u0600-\u06FF\u200C\u200D\s0-9 ]/g, '');
        case 'english':
            return withEnglishNumbers.replace(/[^\u0000-\u007F]/g, '');
        case 'text':
        default:
            return withEnglishNumbers;
    }
};

const Input = forwardRef<HTMLInputElement, FloatingInputProps>(
    (
        {
            containerClassName,
            variant,
            size,
            label = 'لیبل پیشفرض',
            disabled,
            readOnly,
            inputType = 'text',
            onChange,
            name,
            startAdornment,
            endAdornment,
            fullWidth,
            ...props
        },
        ref
    ) => {
        const {
            register,
            formState: { errors }
        } = useFormContext();

        const { onChange: formOnChange, ref: formRef, ...restRegister } = register(name);

        const inputVariant = errors[name] ? 'error' : variant;

        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const rawValue = e.target.value;
            const validatedValue = validateInput(rawValue, inputType);

            if (validatedValue !== rawValue) {
                e.target.value = validatedValue;
            }

            if (onChange) {
                onChange(e);
            }
        };

        return (
            <div className={cn('flex flex-col gap-1', fullWidth && 'w-full')}>
                <div
                    className={cn(
                        containerVariants({ variant: inputVariant, size }),
                        disabled && 'cursor-not-allowed bg-gray-50 opacity-50',
                        containerClassName
                    )}
                >
                    <div className='flex h-full w-full items-center gap-2'>
                        {startAdornment && <div className='flex items-center gap-1'>{startAdornment}</div>}
                        <input
                            className={cn(
                                'peer h-full flex-1 text-base placeholder-transparent focus:ring-0 focus:outline-none'
                            )}
                            placeholder=' '
                            disabled={disabled}
                            readOnly={readOnly}
                            dir='rtl'
                            ref={(el) => {
                                if (typeof ref === 'function') {
                                    ref(el);
                                } else if (ref) {
                                    (ref as React.RefObject<HTMLInputElement | null>).current = el;
                                }
                                formRef(el);
                            }}
                            onChange={(e) => {
                                handleChange(e);
                                formOnChange(e);
                            }}
                            {...props}
                            {...restRegister}
                        />
                        {endAdornment && <div className='flex items-center gap-1'>{endAdornment}</div>}
                        <label
                            className={cn(
                                floatingLabelVariants({ variant: inputVariant, size }),
                                disabled && 'bg-gray-50'
                            )}
                        >
                            {label}
                        </label>
                    </div>
                </div>
                {errors && errors[name] && (
                    <p className='text-right text-sm text-red-500'>{errors[name]?.message as unknown as string}</p>
                )}
            </div>
        );
    }
);

Input.displayName = 'Input';

export default Input;

